import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useTheme,
  useMediaQuery,
  alpha
} from "@mui/material";
import {
  Home,
  School,
  LocalLibrary,
  Forum,
  Person
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isNonMobileScreens = useMediaQuery("(min-width: 1000px)");
  const { user } = useAuth();

  // Don't show on desktop screens
  if (isNonMobileScreens) return null;

  // Hide on full-screen study modes or active lesson view in course page
  const searchParams = new URLSearchParams(location.search);
  const isStudyMode =
    location.pathname.startsWith('/study') ||
    (location.pathname.startsWith('/course/') && (searchParams.has('lesson') || searchParams.has('mode')));

  if (isStudyMode) return null;

  // Helper function to check if a route is active
  const isActive = (path) => {
    if (path === '/home' && (location.pathname === '/' || location.pathname === '/home')) return true;
    if (path === '/courses' && (location.pathname === '/courses' || (location.pathname.startsWith('/course') && !searchParams.has('lesson')))) return true;
    if (path === '/decks' && location.pathname.startsWith('/decks')) return true;
    if (path === '/forum' && location.pathname.startsWith('/forum')) return true;
    if (path === '/profile' && location.pathname.startsWith('/profile')) return true;
    return false;
  };

  // Get current navigation index
  const getCurrentValue = () => {
    if (isActive('/home')) return 0;
    if (isActive('/courses')) return 1;
    if (isActive('/decks')) return 2;
    if (isActive('/forum')) return 3;
    if (isActive('/profile')) return 4;
    return 0;
  };

  const handleNavigation = (event, newValue) => {
    switch (newValue) {
      case 0:
        navigate('/home');
        break;
      case 1:
        navigate('/courses');
        break;
      case 2:
        navigate('/decks');
        break;
      case 3:
        navigate('/forum');
        break;
      case 4:
        if (user?.id) {
          navigate(`/profile/${user.id}`);
        } else {
          navigate('/login');
        }
        break;
      default:
        break;
    }
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        pb: 'calc(6px + env(safe-area-inset-bottom, 0px))',
        pt: '4px',
        px: '6px',
        backgroundColor: isDark
          ? 'rgba(15, 15, 26, 0.88)'
          : 'rgba(255, 255, 255, 0.88)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
        boxShadow: isDark
          ? '0 -4px 20px rgba(0, 0, 0, 0.4)'
          : '0 -4px 20px rgba(0, 0, 0, 0.06)',
        transition: 'transform 0.25s ease-in-out',
        willChange: 'transform',
      }}
    >
      <BottomNavigation
        value={getCurrentValue()}
        onChange={handleNavigation}
        showLabels
        sx={{
          backgroundColor: 'transparent',
          height: '56px',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            flex: 1,
            padding: '4px 2px',
            borderRadius: '12px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            color: isDark ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.55)',
            '&.Mui-selected': {
              color: theme.palette.primary.main,
              backgroundColor: isDark
                ? alpha(theme.palette.primary.main, 0.18)
                : alpha(theme.palette.primary.main, 0.1),
              fontWeight: 700,
              transform: 'scale(1.02)',
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.15)',
                color: theme.palette.primary.main,
              },
            },
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.68rem !important',
            letterSpacing: '-0.01em',
            mt: '2px',
            fontWeight: 500,
            '&.Mui-selected': {
              fontSize: '0.7rem !important',
              fontWeight: 700,
            },
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.35rem',
            transition: 'transform 0.2s ease',
          },
        }}
      >
        <BottomNavigationAction label="Home" icon={<Home />} />
        <BottomNavigationAction label="Courses" icon={<School />} />
        <BottomNavigationAction label="Decks" icon={<LocalLibrary />} />
        <BottomNavigationAction label="Forum" icon={<Forum />} />
        <BottomNavigationAction label="Profile" icon={<Person />} />
      </BottomNavigation>
    </Paper>
  );
};

export default MobileBottomNav;